import { useEffect, useState } from 'react'
import { Radio } from 'lucide-react'
import { useWaveStore } from '../../../stores/wave'
import { useUrgencyStore } from '../../../stores/urgency'
import { CITIES, P_WAVE_KM_S, S_WAVE_KM_S, WAVE_MAX_RADIUS_KM } from '../../../lib/seismic'
import { haversineKm } from '../../../lib/geo'
import { cn } from '../../../lib/utils'

const fmt = (sec: number) => (sec >= 60 ? `${Math.floor(sec / 60)}m${(sec % 60).toFixed(1)}s` : `${sec.toFixed(1)}s`)
/** 全屏紧迫阈值：最近城市 S 波剩余 ≤10s 时进入紧急视觉 */
const URGENCY_THRESHOLD_S = 10

/**
 * P/S 波到达倒计时（地图 overlay，仪器化）：最近城市大号等宽倒计时，
 * 其余城市进度条列表；剩余 ≤10s 同步 urgency store 驱动全屏紧迫视觉。
 * 到达后转红；波扫出最大半径后随 wave 结束自动隐藏。
 */
export function WaveArrivals() {
  const wave = useWaveStore((s) => s.wave)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!wave) return
    const timer = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(timer)
  }, [wave])

  // 紧迫态同步：wave 消失或临近时收敛（setUrgent 内部幂等）
  useEffect(() => {
    let urgent = false
    if (wave) {
      const elapsedS = Math.max(0, (now - wave.originTs) / 1000)
      let minRemaining = Number.POSITIVE_INFINITY
      for (const c of CITIES) {
        const dist = haversineKm(wave.lat, wave.lon, c.lat, c.lon)
        if (dist > WAVE_MAX_RADIUS_KM) continue
        minRemaining = Math.min(minRemaining, dist / S_WAVE_KM_S - elapsedS)
      }
      urgent = minRemaining > 0 && minRemaining <= URGENCY_THRESHOLD_S
    }
    useUrgencyStore.getState().setUrgent(urgent)
  }, [wave, now])

  if (!wave) return null

  const elapsedS = Math.max(0, (now - wave.originTs) / 1000)
  const pRadius = Math.min(WAVE_MAX_RADIUS_KM, elapsedS * P_WAVE_KM_S)
  const sRadius = Math.min(WAVE_MAX_RADIUS_KM, elapsedS * S_WAVE_KM_S)
  const arrivals = CITIES.map((c) => {
    const dist = haversineKm(wave.lat, wave.lon, c.lat, c.lon)
    return { ...c, dist, sArrival: dist / S_WAVE_KM_S, pArrival: dist / P_WAVE_KM_S }
  })
    .filter((c) => c.dist <= WAVE_MAX_RADIUS_KM)
    .sort((a, b) => a.sArrival - b.sArrival)
  const featured = arrivals.find((c) => c.sArrival - elapsedS > 0) ?? arrivals[0]
  const rest = arrivals.filter((c) => c !== featured).slice(0, 7)

  return (
    <div
      className={cn(
        'panel-glass absolute bottom-3 left-3 z-10 w-64 rounded-md',
        featured.sArrival - elapsedS <= URGENCY_THRESHOLD_S && featured.sArrival - elapsedS > 0
          ? 'border-critical/60'
          : '',
      )}
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-1.5 text-[10px] font-medium tracking-[0.2em] text-muted-foreground">
          <Radio size={12} className="animate-pulse text-critical" /> S 波到达倒计时
        </div>
        <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
          P {Math.round(pRadius)}km · S {Math.round(sRadius)}km
        </span>
      </div>
      {featured && (
        <div className="border-b border-border px-3 py-2.5" role="timer" aria-label={`${featured.name} S 波剩余到达时间`}>
          <div className="flex items-baseline justify-between">
            <span className="text-xs text-foreground">{featured.name}</span>
            <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
              距 {Math.round(featured.dist)}km
            </span>
          </div>
          <div
            className={cn(
              'mt-0.5 font-mono text-[26px] font-medium leading-8 tabular-nums',
              featured.sArrival - elapsedS <= URGENCY_THRESHOLD_S ? 'text-critical' : 'text-foreground',
            )}
          >
            {featured.sArrival - elapsedS <= 0 ? '已到达' : `-${fmt(featured.sArrival - elapsedS)}`}
          </div>
          <div className="mt-1 h-1 overflow-hidden rounded-full bg-foreground/[.06]">
            <div
              className={cn(
                'h-full rounded-full transition-[width] duration-250',
                featured.sArrival - elapsedS <= URGENCY_THRESHOLD_S ? 'bg-critical' : 'bg-watch',
              )}
              style={{ width: `${Math.min(100, (elapsedS / featured.sArrival) * 100)}%` }}
            />
          </div>
        </div>
      )}
      <div className="space-y-1 px-3 py-2">
        {rest.map((c) => {
          const remaining = c.sArrival - elapsedS
          const arrived = remaining <= 0
          return (
            <div key={c.name} className="flex items-center gap-2 text-[11px]">
              <span className={cn('w-14 shrink-0', arrived ? 'font-medium text-critical' : 'text-foreground')}>{c.name}</span>
              <div className="relative h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-foreground/[.06]">
                <div
                  className={cn('absolute inset-y-0 left-0 rounded-full', arrived ? 'bg-critical' : 'bg-watch')}
                  style={{ width: `${Math.min(100, (elapsedS / c.sArrival) * 100)}%` }}
                />
              </div>
              <span className={cn('w-16 shrink-0 text-right font-mono tabular-nums', arrived ? 'text-critical' : 'text-muted-foreground')}>
                {arrived ? '已到达' : `-${fmt(remaining)}`}
              </span>
            </div>
          )
        })}
      </div>
      <p className="border-t border-border px-3 py-1.5 font-mono text-[10px] leading-4 text-muted-foreground">
        M{wave.magnitude.toFixed(1)} · S 3.5km/s · 直线距离估算
      </p>
    </div>
  )
}
